import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Video as VideoIcon, Headphones, EyeOff, Plus, RefreshCw,
  CheckCircle, Loader2, Clock, History, Mic, Eye, ChevronDown, Download, Sparkles, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { introsAPI } from "../../services/api";
import GuidedRecorder from "./GuidedRecorder";
import BrandedAudioPlayer from "../BrandedAudioPlayer";

// ───────────────────────────────────────────────────────────────────
// Iter-39 Phase 3 — Control Room (single-panel record/review).
//
// The Introductions surface used to be a 2-tile grid that REPLACED the
// active tile content with GuidedRecorder on record. That swap broke
// the "I am on one screen" mental model the candidate needed —
// especially on phones.
//
// The Control Room consolidates EVERYTHING into a single, focused panel:
//   • Top: compact "both modes at a glance" summary strip.
//   • Mode picker (Video / Audio / Hidden) — single source of truth for
//     which workspace is rendered.
//   • Two-pane workspace for the selected mode (stacked, media-first
//     on mobile so a phone-recorder experience feels native).
//      - LEFT pane (or BOTTOM on mobile): contextual controls, status,
//        "this is the active intro on your QR" badge.
//      - RIGHT pane (or TOP on mobile): media canvas. In idle it's the
//        published intro player; during record/review it's the
//        GuidedRecorder embedded in place — the surrounding chrome
//        never moves.
//   • Below: per-mode default presentation chooser (shared).
//   • Bottom: collapsible history accordion (retired intros).
// ───────────────────────────────────────────────────────────────────

const QUESTIONS_FALLBACK = [
  "Tell us a little about yourself.",
  "Why do you want to work here?",
  "What strengths would you bring to this role?",
  "What schedule are you looking for, and how long are you hoping to work with us?",
  "Is there anything else you'd like the hiring team to know?",
];

const PRESENTATION_MODES = [
  { id: "video", label: "Video", icon: VideoIcon, desc: "Lead with your video intro." },
  { id: "audio", label: "Audio", icon: Headphones, desc: "Lead with your audio intro." },
  { id: "hidden", label: "Hidden", icon: EyeOff, desc: "No intro shown to recruiters." },
];

const MODE_OPTIONS = [
  { id: "video", label: "Video", icon: VideoIcon },
  { id: "audio", label: "Audio", icon: Headphones },
];

const fmtDuration = (s) => {
  if (!s) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

// ─── Compact summary strip ────────────────────────────────────────
// Always-visible chip per mode so the candidate keeps quick visibility
// into BOTH intros at once, even when they're actively working on one.
const SummaryChip = ({ mode, tile, presentationMode }) => {
  const isAudio = mode === "audio";
  const Icon = isAudio ? Headphones : VideoIcon;
  const pub = tile?.published;
  const draft = tile?.draft;
  const isDefault = presentationMode === mode;

  let status; let tone;
  if (pub) {
    status = `Published · ${fmtDuration(pub.duration_seconds)}`;
    tone = "text-emerald-700 bg-emerald-50 border-emerald-200";
  } else if (draft) {
    status = draft.status === "ready_to_publish" ? "Draft ready" :
      draft.status === "stitching" ? "Stitching…" : "Draft in progress";
    tone = "text-amber-700 bg-amber-50 border-amber-200";
  } else {
    status = "Not yet recorded";
    tone = "text-slate-500 bg-slate-50 border-slate-200";
  }

  return (
    <div
      data-testid={`summary-chip-${mode}`}
      className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${tone}`}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="text-slate-900 font-semibold capitalize">{mode}</span>
      <span className="opacity-60">·</span>
      <span>{status}</span>
      {isDefault && (
        <Badge className="bg-blue-100 text-blue-700 ml-1 text-[10px] py-0 px-1.5">
          Default for new QRs
        </Badge>
      )}
    </div>
  );
};

// ─── Media canvas ────────────────────────────────────────────────
// The right pane. Renders one of:
//   • Published intro player (video element OR branded audio card)
//   • Empty-state ("No introduction recorded yet")
// Recording is handled by GuidedRecorder rendered in the parent so
// this stays a pure presentation component.

// Iter-40 Phase 4.1 — owner-only download button for a published
// intro. Pulls the blob via the dedicated endpoint (which sets a
// nice Content-Disposition filename) and triggers a Save-As.
const useDownloadIntro = () => {
  const [downloading, setDownloading] = useState(false);
  const run = useCallback(async (introId) => {
    if (!introId) return;
    setDownloading(true);
    try {
      const { blob, filename } = await introsAPI.downloadIntro(introId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke a tick so Safari has the URL when it starts the download.
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Download started.");
    } catch (err) {
      console.error("intro download failed", err);
      toast.error("Could not download — please try again.");
    } finally {
      setDownloading(false);
    }
  }, []);
  return { downloading, run };
};

const MediaCanvas = ({ mode, tile }) => {
  const isAudio = mode === "audio";
  const pub = tile?.published;
  const download = useDownloadIntro();

  if (!pub) {
    return (
      <div
        data-testid={`media-canvas-${mode}-empty`}
        className="aspect-video bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center text-center p-6"
      >
        {isAudio ? (
          <Headphones className="h-10 w-10 text-slate-300 mb-2" />
        ) : (
          <VideoIcon className="h-10 w-10 text-slate-300 mb-2" />
        )}
        <p className="text-sm font-medium text-slate-600">
          No {isAudio ? "audio" : "video"} introduction recorded yet
        </p>
        <p className="text-xs text-slate-400 mt-1 max-w-xs">
          Click {isAudio ? "Record audio intro" : "Record video intro"} to start. The flow takes about 5 questions.
        </p>
      </div>
    );
  }

  return (
    <div data-testid={`media-canvas-${mode}-published`} className="space-y-3">
      {isAudio ? (
        <div className="rounded-xl bg-gradient-to-br from-indigo-50 via-white to-blue-50 border border-blue-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                <Headphones className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Audio introduction</p>
                <p className="text-[11px] text-slate-500">
                  {fmtDuration(pub.duration_seconds)} · Published {fmtDate(pub.published_at)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-700">
                <CheckCircle className="h-3 w-3 mr-1" /> Live
              </Badge>
            </div>
          </div>
          <BrandedAudioPlayer src={pub.playback_url} durationSeconds={pub.duration_seconds} />
          <div className="mt-3 flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => download.run(pub.id)}
              disabled={download.downloading}
              data-testid={`media-canvas-${mode}-download-btn`}
            >
              {download.downloading
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <Download className="h-3.5 w-3.5 mr-1.5" />}
              Download audio
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="rounded-xl overflow-hidden bg-black border border-slate-200">
            <video
              src={pub.playback_url}
              controls
              playsInline
              preload="metadata"
              className="w-full aspect-video bg-black"
              data-testid={`media-canvas-${mode}-published-player`}
            />
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => download.run(pub.id)}
              disabled={download.downloading}
              data-testid={`media-canvas-${mode}-download-btn`}
            >
              {download.downloading
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <Download className="h-3.5 w-3.5 mr-1.5" />}
              Download video
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Left context pane ────────────────────────────────────────────
// Status, "Currently used by your QR" badge, action buttons (Record /
// Re-record / Continue draft / Preview & publish), and the per-mode
// presentation-mode chooser (folded in so the user can switch defaults
// without scrolling away).
const ContextPane = ({ mode, tile, presentationMode, onStartRecording, onSetMode, modeUpdating, fullState, onDeriveAudio, deriving, onUploadFile, uploading }) => {
  const isAudio = mode === "audio";
  const pub = tile?.published;
  const draft = tile?.draft;
  const isDefault = presentationMode === mode;
  const draftReady = draft?.status === "ready_to_publish";
  const draftFailed = draft?.status === "stitch_failed";
  const fileInputRef = useRef(null);

  // Iter-40 Phase 4.2 — Derive Audio CTA is offered only when the
  // candidate is on the Audio tab AND already has a published video
  // intro AND no audio draft is in progress (so we don't stomp on
  // their typing). It coexists with Record-from-scratch.
  const canDeriveAudio = isAudio
    && !draft
    && !!fullState?.video?.published;

  // Iter-40 Phase 4.3 — Upload existing intro is offered when there's
  // no in-flight draft (mirrors derive-audio's stomp-protection).
  // Available on BOTH video + audio tabs.
  const canUpload = !draft && !uploading;
  const acceptAttr = isAudio
    ? "audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,audio/wave,audio/x-wav,audio/ogg,audio/vorbis,.mp3,.m4a,.wav,.ogg"
    : "video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm";

  const handlePickFile = () => fileInputRef.current?.click();
  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // reset so picking the same file twice still fires onChange
    if (!f) return;
    onUploadFile(f);
  };

  // Per-mode presentation buttons. We surface ALL three (video/audio/
  // hidden) here so the user can change recruiter default in-flow,
  // but disable any option whose intro isn't published yet.
  const renderModeBtn = (m) => {
    const Icon = m.icon;
    const selected = presentationMode === m.id;
    const eligible = m.id === "hidden" ||
      (m.id === "video" && tile?.published && mode === "video") ||
      (m.id === "audio" && tile?.published && mode === "audio") ||
      (m.id === "video" && presentationMode === "video") || // already default → allow re-click
      (m.id === "audio" && presentationMode === "audio") ||
      // also enable a mode if its OWN tile has a published intro
      (m.id === "video" && fullState?.video?.published) ||
      (m.id === "audio" && fullState?.audio?.published);
    return (
      <button
        key={m.id}
        type="button"
        onClick={() => eligible && !selected && onSetMode(m.id)}
        disabled={!eligible || modeUpdating}
        className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold transition-all ${
          selected
            ? "border-blue-500 bg-blue-50 text-blue-700"
            : eligible
              ? "border-slate-200 hover:border-slate-300 bg-white text-slate-700"
              : "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
        }`}
        data-testid={`workspace-mode-${m.id}-btn`}
      >
        <Icon className="h-3.5 w-3.5 inline-block mr-1.5 align-text-bottom" />
        {m.label}
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">
          Status
        </p>
        {pub ? (
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
            <span className="font-semibold text-slate-900">Published</span>
            <span className="text-slate-500">· {fmtDuration(pub.duration_seconds)}</span>
            {isDefault && (
              <Badge className="bg-blue-100 text-blue-700 text-[10px] ml-1">
                On your QR
              </Badge>
            )}
          </div>
        ) : draft ? (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-amber-600" />
            <span className="font-semibold text-amber-900">
              {draftReady ? "Draft ready to preview" : draftFailed ? "Draft stitch failed" : "Draft in progress"}
            </span>
            <span className="text-xs text-slate-500">
              · {(draft.segments || []).length} / 5 answers
            </span>
          </div>
        ) : (
          <div className="text-sm text-slate-500">
            No {isAudio ? "audio" : "video"} introduction yet.
          </div>
        )}
      </div>

      {/* Primary CTA — context-aware */}
      <div>
        {draft ? (
          <Button
            onClick={onStartRecording}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white"
            data-testid={`workspace-${mode}-resume-btn`}
          >
            {draftReady ? <Eye className="h-4 w-4 mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {draftReady ? "Preview & publish" : draftFailed ? "Retry stitch" : "Continue draft"}
          </Button>
        ) : (
          <Button
            onClick={onStartRecording}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            data-testid={`workspace-${mode}-record-btn`}
          >
            {isAudio ? <Mic className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            {pub ? `Re-record ${isAudio ? "audio" : "video"} intro` : `Record ${isAudio ? "audio" : "video"} intro`}
          </Button>
        )}
        {canDeriveAudio && (
          <div className="mt-2">
            <Button
              variant="outline"
              onClick={onDeriveAudio}
              disabled={deriving}
              className="w-full border-blue-200 hover:bg-blue-50 text-blue-700"
              data-testid="workspace-audio-derive-btn"
            >
              {deriving
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Sparkles className="h-4 w-4 mr-2" />}
              {deriving ? "Extracting audio from your video…" : "Create audio version from my video"}
            </Button>
            <p className="text-[11px] text-slate-500 mt-1.5">
              Pulls the audio track from your current video intro. You&apos;ll preview it before publishing — your live intros stay untouched.
            </p>
          </div>
        )}
        {canUpload && (
          <div className="mt-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptAttr}
              onChange={handleFileChange}
              className="hidden"
              data-testid={`workspace-${mode}-upload-input`}
            />
            <Button
              variant="outline"
              onClick={handlePickFile}
              disabled={uploading}
              className="w-full border-slate-300 hover:bg-slate-50 text-slate-700"
              data-testid={`workspace-${mode}-upload-btn`}
            >
              {uploading
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Upload className="h-4 w-4 mr-2" />}
              {uploading
                ? "Uploading your intro…"
                : `Upload existing ${isAudio ? "audio" : "video"} intro`}
            </Button>
            <p className="text-[11px] text-slate-500 mt-1.5">
              {isAudio
                ? "MP3, M4A, WAV, or OGG · max 15 MB · max 25 seconds. You'll preview before publishing."
                : "MP4, MOV, or WebM · max 60 MB · max 90 seconds. You'll preview before publishing."}
            </p>
          </div>
        )}
        {pub && (
          <p className="text-[11px] text-slate-500 mt-2">
            Re-recording creates a new draft. Your live intro stays untouched until you publish.
          </p>
        )}
      </div>

      {/* Default-for-QR chooser (compact, in-flow) */}
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1.5">
          Default for new application packages
        </p>
        <div className="flex gap-2">
          {PRESENTATION_MODES.map(renderModeBtn)}
        </div>
        <p className="text-[11px] text-slate-500 mt-1.5">
          {presentationMode === "hidden"
            ? "Recruiters scanning new QRs will see no intro media."
            : `New QRs will lead with your ${presentationMode} intro.`}
        </p>
      </div>
    </div>
  );
};

const IntroductionsPanel = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [modeUpdating, setModeUpdating] = useState(false);
  // The "workspace mode" — which mode the user is actively managing in
  // the two-pane workspace. Separate from `presentation_mode` (the
  // recruiter default). Defaults to whichever mode has a draft, else
  // the recruiter default, else "video".
  const [workspaceMode, setWorkspaceMode] = useState("video");
  // When true, the right pane shows the GuidedRecorder; when false,
  // it shows the published-intro player / empty state.
  const [recording, setRecording] = useState(false);

  const load = async () => {
    try {
      const data = await introsAPI.getState();
      setState(data);
      // Initialise workspace mode after first load. Don't override
      // user's manual selection later.
      if (!data) return;
      // Wake the workspace into whichever mode is most actionable.
      if (data.video?.draft) setWorkspaceMode("video");
      else if (data.audio?.draft) setWorkspaceMode("audio");
      else if (data.presentation_mode === "audio") setWorkspaceMode("audio");
      else setWorkspaceMode("video");
    } catch (err) {
      console.error("intros load failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSetMode = async (mode) => {
    if (!state || state.presentation_mode === mode) return;
    setModeUpdating(true);
    try {
      await introsAPI.setPresentationMode(mode);
      toast.success(mode === "hidden"
        ? "Set to hidden — recruiters won't see an intro on the public page."
        : `${mode.charAt(0).toUpperCase() + mode.slice(1)} set as default for new application packages.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change default mode.");
    } finally {
      setModeUpdating(false);
    }
  };

  const handleWorkspaceModeChange = (m) => {
    // Don't allow flipping the workspace mode mid-recording; force the
    // user to either publish or cancel first so we don't orphan a
    // running MediaRecorder.
    if (recording) {
      toast.info("Finish or cancel the current recording first.");
      return;
    }
    setWorkspaceMode(m);
  };

  const handleStartRecording = () => setRecording(true);
  const handleEndRecording = () => {
    setRecording(false);
    load();
  };

  // Iter-40 Phase 4.2 — derive an audio draft from the published video.
  // After the server returns the refreshed state, surface the recorder
  // straight into the preview step (the new draft is already
  // ready_to_publish — the recorder auto-jumps to the review screen).
  const [deriving, setDeriving] = useState(false);
  const handleDeriveAudio = async () => {
    setDeriving(true);
    try {
      const newState = await introsAPI.deriveAudioFromVideo();
      setState(newState);
      setWorkspaceMode("audio");
      // Open the recorder so the candidate lands on the preview gate
      // immediately. The PublishConfirmModal will fire on publish
      // because they already have a published audio intro (if any).
      setRecording(true);
      toast.success("Audio extracted — preview before publishing.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not extract audio from your video.";
      toast.error(detail);
    } finally {
      setDeriving(false);
    }
  };

  // Iter-40 Phase 4.3 — Upload an existing intro file. Server enforces
  // strict MIME + size + duration whitelists; we still pre-flight the
  // size client-side so a giant accidental drop fails fast without
  // burning the user's upload budget.
  const [uploading, setUploading] = useState(false);
  const handleUploadFile = async (file) => {
    if (!file) return;
    const mode = workspaceMode;
    const maxBytes = mode === "video" ? 60 * 1024 * 1024 : 15 * 1024 * 1024;
    const maxMb = mode === "video" ? 60 : 15;
    if (file.size > maxBytes) {
      toast.error(
        `That file is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). ${mode === "video" ? "Video" : "Audio"} uploads must be ≤ ${maxMb} MB.`
      );
      return;
    }
    setUploading(true);
    try {
      const newState = await introsAPI.uploadIntro(mode, file);
      setState(newState);
      // Open the recorder which auto-jumps to preview because the
      // upload landed as status="ready_to_publish".
      setRecording(true);
      toast.success("Upload complete — preview before publishing.");
    } catch (err) {
      const detail = err?.response?.data?.detail
        || `Could not upload your ${mode} intro. Please try again.`;
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64" data-testid="intros-loading">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  const activeTile = state?.[workspaceMode];

  return (
    <div className="space-y-6" data-testid="introductions-section">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Introduction</h1>
        <p className="text-slate-600 mt-1 max-w-2xl">
          Publish a video and/or audio introduction. Re-recording creates a draft — your live intro stays untouched until you click <span className="font-semibold">Publish</span>.
        </p>
      </div>

      {/* At-a-glance summary strip — always shows BOTH modes */}
      <div
        className="flex flex-wrap items-center gap-2"
        data-testid="intros-summary-strip"
      >
        <SummaryChip mode="video" tile={state.video} presentationMode={state.presentation_mode} />
        <SummaryChip mode="audio" tile={state.audio} presentationMode={state.presentation_mode} />
        {state.presentation_mode === "hidden" && (
          <div
            data-testid="summary-chip-hidden"
            className="flex items-center gap-2 rounded-full border border-slate-300 bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700"
          >
            <EyeOff className="h-3.5 w-3.5" />
            Recruiter view: hidden
          </div>
        )}
      </div>

      {/* Workspace card */}
      <Card data-testid="intros-workspace" className="border-slate-200 overflow-hidden">
        {/* Workspace mode tabs */}
        <div className="border-b border-slate-200 bg-slate-50/50 px-4 sm:px-6 pt-3">
          <div
            className="inline-flex gap-1 rounded-lg bg-slate-100 p-1"
            role="tablist"
            data-testid="workspace-mode-tabs"
          >
            {MODE_OPTIONS.map((m) => {
              const Icon = m.icon;
              const selected = workspaceMode === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => handleWorkspaceModeChange(m.id)}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-all ${
                    selected
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid={`workspace-tab-${m.id}`}
                >
                  <Icon className="h-4 w-4" />
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>

        <CardContent className="p-4 sm:p-6">
          {recording ? (
            // While recording, the GuidedRecorder takes the full width
            // of the workspace. The surrounding chrome (summary strip,
            // header, history) stays mounted ABOVE/BELOW so the user
            // never loses context — the "single-panel" promise.
            <GuidedRecorder
              mode={workspaceMode}
              questions={state.questions || QUESTIONS_FALLBACK}
              onPublished={handleEndRecording}
              onCancel={handleEndRecording}
            />
          ) : (
            // Idle: two-pane workspace.
            // Desktop: media (right) + context (left) side-by-side.
            // Mobile: media on TOP (the thing the candidate came to
            // see), context BELOW.
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6 lg:gap-8">
              {/* Context pane — order-2 on mobile, order-1 on desktop */}
              <div className="order-2 lg:order-1">
                <ContextPane
                  mode={workspaceMode}
                  tile={activeTile}
                  presentationMode={state.presentation_mode}
                  onStartRecording={handleStartRecording}
                  onSetMode={handleSetMode}
                  modeUpdating={modeUpdating}
                  fullState={state}
                  onDeriveAudio={handleDeriveAudio}
                  deriving={deriving}
                  onUploadFile={handleUploadFile}
                  uploading={uploading}
                />
              </div>
              {/* Media canvas — order-1 on mobile (top), order-2 desktop (right) */}
              <div className="order-1 lg:order-2">
                <MediaCanvas mode={workspaceMode} tile={activeTile} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* History — shared across modes */}
      {state.history && state.history.length > 0 && (
        <Card data-testid="intro-history-card" className="border-slate-200">
          <button
            type="button"
            className="w-full text-left"
            onClick={() => setHistoryOpen((o) => !o)}
            data-testid="intro-history-toggle"
          >
            <CardHeader className="cursor-pointer">
              <CardTitle className="flex items-center justify-between text-base">
                <span className="flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Past versions ({state.history.length})
                </span>
                <ChevronDown
                  className={`h-4 w-4 text-slate-400 transition-transform ${historyOpen ? "rotate-180" : ""}`}
                />
              </CardTitle>
              <CardDescription>
                Retired introductions are kept forever so already-printed package QRs that locked to them keep working.
              </CardDescription>
            </CardHeader>
          </button>
          {historyOpen && (
            <CardContent className="space-y-2 max-h-[480px] overflow-y-auto">
              {state.history.map((h) => (
                <div
                  key={h.id}
                  className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0"
                  data-testid={`intro-history-row-${h.id}`}
                >
                  <div className="flex items-center gap-2">
                    {h.mode === "audio" ? <Headphones className="h-4 w-4 text-slate-400" /> : <VideoIcon className="h-4 w-4 text-slate-400" />}
                    <span className="text-slate-700">
                      {h.mode === "audio" ? "Audio" : "Video"} intro
                    </span>
                  </div>
                  <span className="text-xs text-slate-500">
                    Retired {fmtDate(h.retired_at)} · {fmtDuration(h.duration_seconds)}
                  </span>
                </div>
              ))}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};

export default IntroductionsPanel;
